import {
	resolveTableViewState,
	Table,
	TableCard,
	TableRowActions,
	TableStateRow,
} from "@components";
import { dayjs } from "@lib";
import { useConfirmModal } from "@providers";
import { Edit01, Eye, Trash01 } from "@untitledui/icons";
import type { ReactNode } from "react";
import { TruncatedText } from "@/shared/components/truncated-text";
import {
	EXPENSES_HISTORY_COLUMNS,
	REPORT_SUMMARY_CATEGORY_NAMES,
} from "../constants";
import { useDeleteExpense } from "../hooks";
import type { ExpensesPaginatedResponseItem } from "../types";
import { formatCurrency } from "../utils";
import type { ExpenseModalMode } from "./new-expense-modal";

type ExpenseModalProps = {
	mode: ExpenseModalMode;
	id: string | null;
};

type ExpensesFilteredTableProps = {
	items: ExpensesPaginatedResponseItem[];
	onEdit: (expenseModal: ExpenseModalProps) => void;
	isLoading?: boolean;
	isError?: boolean;
	onRetry?: () => void;
	footer?: ReactNode;
};

function ExpensesFilteredHistoryTableHead({
	column,
}: {
	column: (typeof EXPENSES_HISTORY_COLUMNS)[number];
}) {
	return (
		<Table.Head
			id={column.id}
			isRowHeader={EXPENSES_HISTORY_COLUMNS.indexOf(column) === 0}
			key={column.id}
			label={column.label}
			style={{
				...(!!column?.minWidth && {
					minWidth: `${column.minWidth}px`,
				}),
			}}
		/>
	);
}

function ExpensesFilteredHistoryTableRow({
	item,
	onEdit,
	onDelete,
}: {
	item: ExpensesPaginatedResponseItem;
	onEdit: (expenseModal: ExpenseModalProps) => void;
	onDelete: (expenseId: number) => Promise<void>;
}) {
	const vehicleLabel =
		!!item.brand && !!item.model ? `${item.brand} ${item.model}` : "-";

	return (
		<Table.Row
			className="last:border-b-0"
			columns={EXPENSES_HISTORY_COLUMNS}
			id={item.expenseId}
		>
			<Table.Cell className="whitespace-nowrap font-semibold">
				{item.date ? dayjs(item.date).format("DD/MM/YYYY") : "-"}
			</Table.Cell>
			<Table.Cell className="whitespace-nowrap font-semibold">
				{item.plate ? item.plate : "-"}
			</Table.Cell>
			<Table.Cell className="text-tertiary">{vehicleLabel}</Table.Cell>
			<Table.Cell className="font-semibold">
				{REPORT_SUMMARY_CATEGORY_NAMES[item.type]}
			</Table.Cell>
			<Table.Cell className="min-w-[320px] text-tertiary">
				<TruncatedText maxWidth={300} text={item.description} />
			</Table.Cell>
			<Table.Cell className="whitespace-nowrap font-semibold">
				{item.amount ? formatCurrency(item.amount) : "-"}
			</Table.Cell>
			<Table.Cell>
				<TableRowActions
					actions={[
						{
							id: "view",
							label: "Visualizar",
							icon: Eye,
							onClick: () => {
								onEdit({ mode: "view", id: String(item.expenseId) });
							},
						},
						{
							id: "edit",
							label: "Editar",
							icon: Edit01,
							onClick: () => {
								onEdit({ mode: "edit", id: String(item.expenseId) });
							},
						},
						{
							id: "delete",
							label: "Excluir",
							icon: Trash01,
							onClick: () => {
								onDelete(item.expenseId);
							},
						},
					]}
					label={`Ações da despesa de ${item.plate ?? "veículo sem placa"}`}
				/>
			</Table.Cell>
		</Table.Row>
	);
}

export function ExpensesFilteredHistoryTable({
	items,
	onEdit,
	isLoading = false,
	isError = false,
	onRetry,
	footer,
}: ExpensesFilteredTableProps) {
	const { confirm } = useConfirmModal();
	const deleteMutation = useDeleteExpense();
	const view = resolveTableViewState({
		itemCount: items.length,
		isLoading,
		isError,
	});

	const handleDelete = async (expenseId: number) => {
		const result = await confirm({
			title: "Excluir despesa",
			description:
				"Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.",
			cancelText: "Cancelar",
			confirmText: "Excluir",
			variant: "warning",
			confirmVariant: "danger",
		});

		if (result) {
			await deleteMutation.mutateAsync(expenseId);
		}
	};

	return (
		<TableCard.Root variant="listing">
			<Table
				aria-label="Histórico de despesas"
				isRefreshing={view.isRefreshing}
				selectionMode="none"
			>
				<Table.Header columns={EXPENSES_HISTORY_COLUMNS}>
					{(column) => <ExpensesFilteredHistoryTableHead column={column} />}
				</Table.Header>

				<Table.Body>
					{view.view === "data" ? (
						<>
							{view.hasRefreshError ? (
								<TableStateRow
									colSpan={EXPENSES_HISTORY_COLUMNS.length}
									onRetry={onRetry}
									state="refresh-error"
								/>
							) : null}
							{items.map((item) => (
								<ExpensesFilteredHistoryTableRow
									item={item}
									key={item.expenseId}
									onDelete={handleDelete}
									onEdit={onEdit}
								/>
							))}
						</>
					) : (
						<TableStateRow
							colSpan={EXPENSES_HISTORY_COLUMNS.length}
							emptyContent="Nenhuma despesa encontrada para os filtros selecionados."
							errorMessage="Não foi possível carregar o histórico de despesas."
							loadingLabel="Carregando histórico de despesas"
							onRetry={onRetry}
							state={view.view}
						/>
					)}
				</Table.Body>
			</Table>
			{footer}
		</TableCard.Root>
	);
}
